/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：マップサービス
 *
 * 作成者：2021/12/01 ノア）虞
 * 最終修正者：2021/12/02 ノア）虞
 *
 */
import { toNumber } from 'lodash'

import { ManagementItem, ManagementPlaceMapData, Position, AddressInfo, DynamicHistory } from '@/models'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { i18n } from '@/locales'
import { DeviceStatus } from '@/commons/constants'

/**
 * 地図を描くオブジェクトの幅と高
 */
const MapObjectWidthAndHeight = 34
export class MapService {
  /**
   * 地図のインスタンス
   *
   * @private
   */
  // eslint-disable-next-line
  private zdcMap: any

  /**
   * 地図の矢印線
   *
   * @private
   */
  // eslint-disable-next-line
  private arrowPl: any[] = []

  /**
   * 地図の物体
   *
   * @private
   */
  // eslint-disable-next-line
  private groundObject: Record<string, any> | null = null

  /**
   * 地図の地点
   *
   * @private
   */
  // eslint-disable-next-line
  private groundPlace: Record<string, any> | null = null

  /**
   * 地図の写真
   *
   * @private
   */
  // eslint-disable-next-line
  private groundImage: Record<string, any> | null = null

  /**
   * 地図の位置情報
   *
   * @private
   */
  // eslint-disable-next-line
  private groundLocationPoint: Record<string, any> | null = null

  /**
   * 地図のダイアログ
   *
   * @private
   */
  // eslint-disable-next-line
  private groundDialog: Record<string, any> = {}

  /**
   * 地図地点のダイアログ
   *
   * @private
   */
  // eslint-disable-next-line
  private groundPlaceDialog: Record<string, any> = {}

  // eslint-disable-next-line
  private listMessageDialog: any

  /**
   * サーバーからメッセージを受信したときに呼び出されるコールバック関数
   */
  private onClickLatlon?: (latlon: Position) => void

  /**
   * 管理対象データ
   */
  private dynamicTargetList: ManagementItem[] = []

  /**
   * 地図の物体
   *
   * @private
   */
  // eslint-disable-next-line
  private plotPoints: Record<string, any> | null = null

  /**
   * 地図をクリックしたとき
   *
   * @private
   */
  // eslint-disable-next-line
  private clickedPoint: any | null = null

  /**
   * 地図の初期化処理を実行する
   *
   * @param {HTMLDivElement} mapContainer 地図のコンテナー
   * @param {Position} centerLatLon 地図のコンテナー
   * @param {any} onClickLatlon callback
   * @returns {void}
   */
  init(
    mapContainer: HTMLDivElement,
    centerLatLon: Position = { lat: 35.6778614, lng: 139.7703167 },
    onClickLatlon?: (latlon: Position) => void
  ): void {
    if (!ZDC.LatLon) return

    this.onClickLatlon = onClickLatlon
    const centerll = new ZDC.LatLon(centerLatLon.lat, centerLatLon.lng)
    const mapOptions = {
      latlon: centerll,
      zoom: 10,
      mapType: ZDC.MAPTYPE_DEFAULT
    }

    this.zdcMap = new ZDC.Map(mapContainer, mapOptions)

    /* スケールバーを作成 */
    this.zdcMap?.addWidget(new ZDC.ScaleBar())

    /* 通常のコントロールを作成 */
    const normalWidget = new ZDC.Control({ pos: { top: 10, left: 10 }, type: ZDC.CTRL_TYPE_NORMAL })

    /* コントロールを表示 */
    this.zdcMap?.addWidget(normalWidget)

    /* 地図をクリックしたときの動作 */
    ZDC.addListener(this.zdcMap, ZDC.MAP_CLICK, () => {
      this.getClickLatLon()
    })

    setTimeout(() => {
      window.onresize = () => {
        this.mapRefresh()
      }
    }, 500)
  }

  /**
   * 地図領域の大きさを変更する
   *
   * @returns {void}
   */
  mapRefresh(): void {
    if (this.zdcMap) {
      this.zdcMap?.refresh()
    }
  }

  /**
   * クリックした地点の緯度経度を表示する
   *
   * @returns {void}
   */
  // eslint-disable-next-line
  getClickLatLon(): void {
    const latlon = this.zdcMap?.getPointerPosition()

    this.markClickedPoint(latlon)

    const currentZoom = this.zdcMap?.getZoom()
    let scaleNum
    switch (currentZoom) {
      case 0:
        scaleNum = 20000000
        break
      case 1:
        scaleNum = 10000000
        break
      case 2:
        scaleNum = 5000000
        break
      case 3:
        scaleNum = 2500000
        break
      case 4:
        scaleNum = 1500000
        break
      case 5:
        scaleNum = 650000
        break
      case 6:
        scaleNum = 350000
        break
      case 7:
        scaleNum = 150000
        break
      case 8:
        scaleNum = 80000
        break
      case 9:
        scaleNum = 40000
        break
      case 10:
        scaleNum = 20000
        break
      case 11:
        scaleNum = 10000
        break
      case 12:
        scaleNum = 5000
        break
      case 13:
        scaleNum = 2500
        break
      case 14:
        scaleNum = 1500
        break
      default:
        scaleNum = 40000
        break
    }
    const referenceDistance = scaleNum * (MapObjectWidthAndHeight / 2 / 10000)

    // 重なり合うオブジェクト
    const overlappingManagements: ManagementItem[] = []
    this.dynamicTargetList.forEach(item => {
      const managementLatlon = new ZDC.LatLon(item.lat, item.lng)
      const dist = ZDC.getLatLonToLatLonDistance(latlon, managementLatlon)
      if (dist <= referenceDistance) {
        overlappingManagements.push(item)
      }
    })

    if (overlappingManagements.length === 1) {
      this.openObjectDialog(overlappingManagements[0])
    }
    if (overlappingManagements.length > 1) {
      this.openObjectListDialog(overlappingManagements, latlon)
    }

    const paramLatLon = { lat: latlon.lat, lng: latlon.lon }
    // eslint-disable-next-line
    this.searchAddressInfoByLatlon(paramLatLon, (res: any) => {
      if (this.onClickLatlon) {
        this.onClickLatlon(res)
      }
    })
  }

  /**
   * 地図の地点を描画する
   *
   * @param {ManagementPlaceMapData} parkingDataItem 管理場所
   * @returns {void}
   */
  markPlace(parkingDataItem: ManagementPlaceMapData): void {
    if (!ZDC.LatLon) return

    if (!parkingDataItem || !parkingDataItem.id) {
      return
    }

    if (!this.groundPlace) {
      this.groundPlace = {}
    }

    const objectLatLon = new ZDC.LatLon(parkingDataItem.latUpperLeft, parkingDataItem.lngUpperLeft)

    /* ユーザウィジットを作成 */
    const options = this.getPlaceOptions(parkingDataItem)

    this.groundPlace[parkingDataItem.id] = new ZDC.UserWidget(objectLatLon, options)

    /* ユーザウィジットを追加 */
    this.zdcMap?.addWidget(this.groundPlace[parkingDataItem.id])
    /* ユーザウィジットを表示 */
    this.groundPlace[parkingDataItem.id]?.open()

    ZDC.addListener(this.groundPlace[parkingDataItem.id], ZDC.USERWIDGET_CLICK, () => {
      this.getClickLatLon()
      this.openPlaceDialog(parkingDataItem)
    })
  }

  /**
   * 地図の地点を削除する
   *
   * @param {ManagementPlaceMapData} parkingDataItem 管理場所
   * @returns {void}
   */
  removePlace(parkingDataItem: ManagementPlaceMapData): void {
    if (!ZDC.LatLon) return

    if (!parkingDataItem || !parkingDataItem.id) {
      return
    }
    if (!this.groundPlace) {
      this.groundPlace = {}
    }
    this.zdcMap?.removeWidget(this.groundPlace[parkingDataItem.id])
  }

  /**
   * 地図の物体を描画する
   *
   * @param {ManagementItem} trailerDataItem 管理対象
   * @returns {void}
   */
  markObject(trailerDataItem: ManagementItem): void {
    if (!ZDC.LatLon) return

    if (!trailerDataItem) {
      return
    }

    if (!this.groundObject) {
      this.groundObject = {}
    }

    if (this.groundObject[trailerDataItem.manageTargetId]) {
      this.zdcMap?.removeWidget(this.groundObject[trailerDataItem.manageTargetId])
    }

    const objectLatLon = new ZDC.LatLon(trailerDataItem.lat, trailerDataItem.lng)

    /* ユーザウィジットを作成 */
    const options = this.getObjectOptions(trailerDataItem)

    this.groundObject[trailerDataItem.manageTargetId] = new ZDC.UserWidget(objectLatLon, options)

    /* ユーザウィジットを追加 */
    this.zdcMap?.addWidget(this.groundObject[trailerDataItem.manageTargetId])
    /* ユーザウィジットを表示 */
    this.groundObject[trailerDataItem.manageTargetId]?.open()

    ZDC.addListener(this.groundObject[trailerDataItem.manageTargetId], ZDC.USERWIDGET_CLICK, () => {
      this.getClickLatLon()
    })
  }

  /**
   * 地図の物体を削除する
   *
   * @param {ManagementItem} trailerDataItem 管理対象
   * @returns {void}
   */
  removeObject(trailerDataItem: ManagementItem): void {
    if (!ZDC.LatLon) return

    if (!trailerDataItem) {
      return
    }

    if (!this.groundObject) {
      this.groundObject = {}
    }
    this.zdcMap?.removeWidget(this.groundObject[trailerDataItem.manageTargetId])
  }

  markLocationPoint(addressInfo: AddressInfo): void {
    if (!ZDC.LatLon) return

    if (!addressInfo || !addressInfo.addressPosition) {
      return
    }

    if (!this.groundLocationPoint) {
      this.groundLocationPoint = {}
    }

    if (!this.groundLocationPoint[addressInfo.addressName]) {
      this.groundLocationPoint[addressInfo.addressName] = {}
    }

    this.removeLocationPoint()

    const objectLatLon = new ZDC.LatLon(addressInfo.addressPosition.lat, addressInfo.addressPosition.lng)

    /* マーカを作成 */
    this.groundLocationPoint[addressInfo.addressName].point = new ZDC.Marker(objectLatLon, {
      propagation: true,
      custom: {
        base: {
          src: '/images/point2.png'
        }
      }
    })

    /* マーカを追加 */
    this.zdcMap?.addWidget(this.groundLocationPoint[addressInfo.addressName].point)

    const widgetlabel = {
      offset: new ZDC.Pixel(17, -2),
      propagation: true,
      html: '<div style="background-color: #FFFFFF"><b>' + addressInfo.addressName + '</b></div>'
    }
    this.groundLocationPoint[addressInfo.addressName].name = new ZDC.UserWidget(objectLatLon, widgetlabel)

    /* ユーザウィジットを追加 */
    this.zdcMap?.addWidget(this.groundLocationPoint[addressInfo.addressName].name)

    /* ユーザウィジットを表示 */
    this.groundLocationPoint[addressInfo.addressName]?.name?.open()

    const paramLatLon = { lat: objectLatLon.lat, lon: objectLatLon.lon }
    this.zdcMap?.moveLatLon(paramLatLon)
  }

  /**
   * 地図地点バウンド情報を開く
   *
   * @param {ManagementPlaceMapData} parkingDataItem 管理場所
   * @param {isMoveCenter} isMoveCenter 地図の中心点が移動するかどうか
   * @returns {void}
   */
  openPlaceDialog(parkingDataItem: ManagementPlaceMapData, isMoveCenter = false): void {
    if (!parkingDataItem || !parkingDataItem.id) {
      return
    }

    if (isMoveCenter) {
      if (parkingDataItem && parkingDataItem.latUpperLeft && parkingDataItem.latUpperLeft) {
        const paramLatLon = { lat: parkingDataItem.latUpperLeft, lon: parkingDataItem.lngUpperLeft }
        this.zdcMap?.moveLatLon(paramLatLon)
      }
    }

    this.closeAllDialog()
    this.groundPlaceDialog[parkingDataItem.id]?.open()
  }

  /**
   * 地図の対象情報を開く
   *
   * @param {ManagementItem} trailerDataItem 管理対象
   * @param {isMoveCenter} isMoveCenter 地図の中心点が移動するかどうか
   * @returns {void}
   */
  openObjectDialog(trailerDataItem: ManagementItem, isMoveCenter = false): void {
    if (isMoveCenter) {
      if (trailerDataItem) {
        const paramLatLon = { lat: trailerDataItem.lat, lon: trailerDataItem.lng }
        this.zdcMap?.moveLatLon(paramLatLon)
      }
    }

    this.closeAllDialog()
    this.groundDialog[trailerDataItem.manageTargetId]?.open()
    this.setManagementIndex(trailerDataItem.manageTargetId)
  }

  /**
   * 地図の対象リスト情報を開く
   *
   * @param {ManagementItem[]} trailerData 管理対象リスト
   * @param {any} currentLatLon クリックした地点の緯度経度
   * @returns {void}
   */
  // eslint-disable-next-line
  openObjectListDialog(trailerData: ManagementItem[], currentLatLon: any): void {
    this.closeAllDialog()
    this.markObjectList(trailerData, currentLatLon)
  }

  /**
   * 地図の対象リスト情報を描画する
   *
   * @param {ManagementItem[]} trailerData 管理対象リスト
   * @param {any} currentLatLon クリックした地点の緯度経度
   * @returns {void}
   */
  // eslint-disable-next-line
  markObjectList(trailerData: ManagementItem[], currentLatLon: any): void {
    /* ユーザウィジットを作成 */
    const options = this.getMessageListOptions(trailerData)

    this.listMessageDialog = new ZDC.MsgInfo(currentLatLon, options)

    /* ユーザウィジットを追加 */
    this.zdcMap?.addWidget(this.listMessageDialog)

    this.listMessageDialog?.open()

    const listTableTbodyDom = document.getElementById('tti-map-table-tbody')
    // eslint-disable-next-line
    listTableTbodyDom?.addEventListener('click', (ev: any) => {
      let target = ev.target
      while (target !== listTableTbodyDom) {
        if (target.tagName.toLowerCase() == 'tr') {
          const splitIdArr = target.id.split(':')
          if (splitIdArr.length > 1) {
            const listIndex = toNumber(splitIdArr[1])
            if (trailerData[listIndex]) {
              this.closeAllDialog()
              this.openObjectDialog(trailerData[listIndex])
            }
          }
          break
        }
        target = target.parentNode
      }
    })

    const mapOverlappling = document.getElementById('tti-map-overlappling')
    const mapTable = document.getElementById('tti-map-table')
    const maxTopNumber = 56 * trailerData.length
    const topNumber = -(maxTopNumber - 245)
    let currentTop = 0
    // eslint-disable-next-line
    mapOverlappling?.addEventListener('mousewheel', (ev: any) => {
      if (ev.deltaY > 0) {
        if (currentTop >= topNumber + 46.3448) {
          currentTop = currentTop - 46.3448
        }
      } else {
        if (currentTop <= -46.3448) {
          currentTop = currentTop + 46.3448
        }
      }
      if (mapTable) mapTable.style.top = currentTop + 'px'
    })
  }

  /**
   * すべての地図弾窓を閉じる
   *
   * @returns {void}
   */
  closeAllDialog(): void {
    this.listMessageDialog?.close()

    for (const key in this.groundDialog) {
      const dialog = this.groundDialog[key]
      dialog.close()
    }

    for (const key in this.groundPlaceDialog) {
      const dialog = this.groundPlaceDialog[key]
      dialog.close()
    }
  }

  removeLocationPoint(): void {
    for (const key in this.groundLocationPoint) {
      const point = this.groundLocationPoint[key].point
      const name = this.groundLocationPoint[key].name
      if (point) {
        this.zdcMap?.removeWidget(point)
      }
      if (name) {
        this.zdcMap?.removeWidget(name)
      }
    }
  }

  removePlotPoints(): void {
    for (const key in this.plotPoints) {
      const point = this.plotPoints[key].point
      const name = this.plotPoints[key].name
      if (point) {
        this.zdcMap?.removeWidget(point)
      }
      if (name) {
        this.zdcMap?.removeWidget(name)
      }
    }
  }

  /**
   * 地図のバウンド情報を描画する
   *
   * @param {ManagementItem} trailerDataItem 管理対象
   * @returns {void}
   */
  markMessage(trailerDataItem: ManagementItem): void {
    if (!ZDC.LatLon) return

    if (!trailerDataItem) {
      return
    }

    if (!this.groundDialog) {
      this.groundDialog = {}
    }

    if (this.groundDialog[trailerDataItem.manageTargetId]) {
      this.zdcMap?.removeWidget(this.groundDialog[trailerDataItem.manageTargetId])
    }

    const objectLatLon = new ZDC.LatLon(trailerDataItem.lat, trailerDataItem.lng)

    /* ユーザウィジットを作成 */
    const options = this.getMessageOptions(trailerDataItem)

    this.groundDialog[trailerDataItem.manageTargetId] = new ZDC.MsgInfo(objectLatLon, options)

    /* ユーザウィジットを追加 */
    this.zdcMap?.addWidget(this.groundDialog[trailerDataItem.manageTargetId])
  }

  /**
   * 地図地点のバウンド情報を描画する
   *
   * @param {ManagementPlaceMapData} parkingDataItem 管理場所
   * @returns {void}
   */
  markPlaceMessage(parkingDataItem: ManagementPlaceMapData): void {
    if (!ZDC.LatLon) return

    if (!parkingDataItem || !parkingDataItem.id) {
      return
    }

    if (!this.groundPlaceDialog) {
      this.groundPlaceDialog = {}
    }

    const objectLatLon = new ZDC.LatLon(parkingDataItem.latUpperLeft, parkingDataItem.lngUpperLeft)

    /* ユーザウィジットを作成 */
    const options = this.getPlaceMessageOptions(parkingDataItem)

    this.groundPlaceDialog[parkingDataItem.id] = new ZDC.MsgInfo(objectLatLon, options)

    /* ユーザウィジットを追加 */
    this.zdcMap?.addWidget(this.groundPlaceDialog[parkingDataItem.id])
  }

  /**
   * 地図を描く地点の構成を取得する
   *
   * @param {ManagementPlaceMapData} parkingDataItem 管理場所
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getPlaceOptions(parkingDataItem: ManagementPlaceMapData) {
    if (!parkingDataItem) {
      return {}
    }

    const iconSrc = '/icons/wo_027.svg'

    const htmlSrc = `<i class="tti-icon-common tti-icon-place-common"><img src=${iconSrc} /></i>`

    return {
      html: htmlSrc,
      size: new ZDC.WH(41, 37),
      offset: new ZDC.Pixel(-20, -18)
    }
  }

  /**
   * 地図を描くオブジェクトの構成を取得する
   *
   * @param {ManagementItem} trailerDataItem 管理対象
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getObjectOptions(trailerDataItem: ManagementItem) {
    if (!trailerDataItem) {
      return {}
    }

    const usageIconSrc = '/icons/wo_049.svg'
    let deviceStatus = '</div>'
    switch (trailerDataItem.deviceStatus) {
      case DeviceStatus.POWER_ON:
        deviceStatus = `<div style="height: 4px;"></div><div style="text-align: center;font-size:10px;">電源</div><div style="text-align: center; margin-top: -4px;font-size:10px;">ON</div></div>`
        break

      case DeviceStatus.STOP_DETECTION:
        deviceStatus = `<div style="height: 4px;"></div><div style="text-align: center;margin-top: 4px;font-size:10px;">停止</div></div>`
        break

      case DeviceStatus.START_OPERATION:
        deviceStatus = `<div style="height: 4px;"></div><div style="text-align: center;font-size:10px;">稼働</div><div style="text-align: center; margin-top: -4px;font-size:10px;">開始</div></div>`
        break

      case DeviceStatus.POWER_OFF:
        deviceStatus = `<div style="height: 4px;"></div><div style="text-align: center;font-size:10px;">電源</div><div style="text-align: center; margin-top: -4px;font-size:10px;">OFF</div></div>`
        break

      case DeviceStatus.PERIODIC_OUTAGE:
        deviceStatus = `<div style="height: 4px;"></div><div style="text-align: center;font-size:10px;">停</div><div style="text-align: center; margin-top: -4px;font-size:10px;">止中</div></div>`
        break

      case DeviceStatus.PERIODIC_DURING_OPERATION:
        deviceStatus = `<div style="height: 4px;"></div><div style="text-align: center;font-size:10px;">稼</div><div style="text-align: center; margin-top: -4px;font-size:10px;">働中</div></div>`
        break

      default:
        break
    }

    let htmlSrc = `<i class="tti-icon-common tti-icon-walk-common"><img src=${usageIconSrc} /></i>`
    htmlSrc += `<div style="color: #ffffff;" class="ttp-workStatus-common ttp-workStatus-css2">`
    htmlSrc += deviceStatus

    return {
      html: htmlSrc,
      // 地図を描くオブジェクトの幅と高
      size: new ZDC.WH(MapObjectWidthAndHeight, MapObjectWidthAndHeight),
      offset: new ZDC.Pixel(-17, -17)
    }
  }

  /**
   * 地図弾窓を描く構成を取得する
   *
   * @param {ManagementItem} trailerDataItem 管理対象
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMessageOptions(trailerDataItem: ManagementItem) {
    if (!trailerDataItem) {
      return {}
    }

    let htmlSrc = '<div class="tti-map-dialog"><section class="map-message-title">'
    htmlSrc += `<p style="width:340px">${trailerDataItem.manageTargetName ?? ''}(${
      trailerDataItem.manageTargetCode ?? ''
    })</p>`
    htmlSrc += `<p style="width:340px">${trailerDataItem.sigfoxId ?? ''}</p>`
    htmlSrc += `<p style="width:340px">${trailerDataItem.businessOfficeName ?? ''}</p></section>`
    htmlSrc += '<article><p style="margin-top: -1px; max-width: 340px">'
    htmlSrc += `<span class="tti-map-label">${i18n.t('dynamicMap.dateTime')}</span>`
    htmlSrc += `<span class="tti-map-value">${trailerDataItem.dateTime ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 340px">'
    htmlSrc += `<span class="tti-map-label">${i18n.t('dynamicMap.deviceStatus')}</span>`
    htmlSrc += `<span class="tti-map-value">${trailerDataItem.deviceStatusName ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 340px">'
    htmlSrc += `<span class="tti-map-label">${i18n.t('dynamicMap.latesUpdateDate')}</span>`
    htmlSrc += `<span class="tti-map-value">${trailerDataItem.lastUpdateDate ?? ''}</span></p>`
    htmlSrc += `<span class="tti-map-label">${i18n.t('dynamicMap.powerSavingMode')}</span>`
    htmlSrc += `<span class="tti-map-value">${trailerDataItem.powerSavingModeName ?? ''}</span></p></article>`
    htmlSrc += '<article style="border-bottom: none"><p style="margin-top: -1px; max-width: 340px">'
    htmlSrc += `<span class="tti-map-label">${i18n.t('dynamicMap.lat')}</span>`
    htmlSrc += `<span class="tti-map-value">${trailerDataItem.latForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 340px">'
    htmlSrc += `<span class="tti-map-label">${i18n.t('dynamicMap.lng')}</span>`
    htmlSrc += `<span class="tti-map-value">${trailerDataItem.lngForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 340px">'
    htmlSrc += `<span class="tti-map-label">${i18n.t('dynamicMap.address')}</span>`
    htmlSrc += `<span class="tti-map-value">${trailerDataItem.location ?? ''}</span></p></article>`

    return {
      html: htmlSrc
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMessageListOptions(trailerData: ManagementItem[]) {
    let htmlSrc =
      '<div class="tti-map-overlappling" id="tti-map-overlappling"><table id="tti-map-table" class="tti-map-table"><tbody id="tti-map-table-tbody">'
    trailerData.forEach((item, index) => {
      htmlSrc += `<tr style="height:56px;padding:4px 0" id="tti-map-table-tr:${index}"><td align="left" style="width:190px">`
      htmlSrc += `<div><label style="width:190px;line-height:20px;">${item.manageTargetName}</label></div>`
      htmlSrc += `<div><label style="width:190px;line-height:20px; color:#666;">${item.manageTargetCode}</label></div></td>`
      htmlSrc += `<td align="left"><label style="width:90px;">${item.deviceStatusName}</label></td></tr>`
    })
    htmlSrc += '</tbody></table></div>'

    return {
      html: htmlSrc
    }
  }

  /**
   * 地図地点弾窓を描く構成を取得する
   *
   * @param {ManagementPlaceMapData} parkingDataItem 管理場所
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getPlaceMessageOptions(parkingDataItem: ManagementPlaceMapData) {
    if (!parkingDataItem) {
      return {}
    }

    let htmlSrc = '<div class="tti-map-dialog"><section class="map-message-title">'
    htmlSrc += `<p style="width:290px">${parkingDataItem.managementPlaceName ?? ''}(${
      parkingDataItem.managementPlaceCode ?? ''
    })</p>`
    htmlSrc += `</section>`
    htmlSrc += '<article><p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">エリア</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.areaName ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">都道府県</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.prefectureName ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">住所</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.location ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">緯度(左上)</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.latUpperLeftForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">経度(左上)</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.lngUpperLeftForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">緯度(右上)</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.latUpperRightForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">経度(右上)</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.lngUpperRightForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">緯度(右下)</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.latLowerRightForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">経度(右下)</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.lngLowerRightForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">緯度(左下)</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.latLowerLeftForPage ?? ''}</span></p>`
    htmlSrc += '<p style="margin-top: -1px; max-width: 290px">'
    htmlSrc += '<span class="tti-map-label">経度(左下)</span>'
    htmlSrc += `<span class="tti-map-value">${parkingDataItem.lngLowerLeftForPage ?? ''}</span></p></article>`

    return {
      html: htmlSrc
    }
  }

  /**
   * 矢印線を描画する
   *
   * @param {Position[]} positions 座標セット
   * @returns {void}
   */
  async drawPolylineWithArrow(positions: Position[]): Promise<void> {
    this.removePolylineWithArrow()

    if (!positions || positions.length === 0) {
      return
    }
    const options = {
      strokeColor: '#00E300',
      strokeWeight: 4
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const roadLatlons: any[] = []
    positions.forEach(item => {
      roadLatlons.push(new ZDC.LatLon(item.lat, item.lng))
    })

    this.arrowPl.push(new ZDC.ArrowPolyline(this.zdcMap, roadLatlons, options))
  }

  /**
   * 経由地点文字列を作成する
   *
   * @param {Position[]} mpointList 経由地点ルート
   * @returns {string} 経由地点文字列
   */
  createMpointStr(mpointList: Position[]): string {
    mpointList.shift()
    mpointList.pop()
    if (mpointList.length === 0) {
      return ''
    }
    let mpointStr = ''
    mpointList.forEach((mpoint, index) => {
      if (index === mpointList.length - 1) {
        mpointStr = mpointStr + mpoint.lat + ',' + mpoint.lng
      } else {
        mpointStr = mpointStr + mpoint.lat + ',' + mpoint.lng + ','
      }
    })
    return mpointStr
  }

  /**
   * 矢印線を削除する
   *
   * @returns {void}
   */
  removePolylineWithArrow(): void {
    if (this.arrowPl && this.arrowPl.length > 0) {
      this.arrowPl.forEach(item => {
        item.remove()
      })

      this.arrowPl = []
    }
  }

  /**
   * 写真を描画する
   *
   * @param {ManagementPlaceMapData} parkingDataItem 管理場所
   * @returns {void}
   */
  drawImage(parkingDataItem: ManagementPlaceMapData): void {
    if (!parkingDataItem || !parkingDataItem.id || !parkingDataItem.imagePath) {
      return
    }

    if (!this.groundImage) {
      this.groundImage = {}
    }

    const upperLeftLatLon = new ZDC.LatLon(parkingDataItem.latUpperLeft, parkingDataItem.lngUpperLeft)
    const upperRightLatLon = new ZDC.LatLon(parkingDataItem.latUpperRight, parkingDataItem.lngUpperRight)
    const lowerRightLatLon = new ZDC.LatLon(parkingDataItem.latLowerRight, parkingDataItem.lngLowerRight)

    // eslint-disable-next-line
    const latlons: any[] = [upperLeftLatLon, upperRightLatLon, lowerRightLatLon]

    const imageWidth = this.parallelogramWidthCalculation(this.zdcMap, latlons)
    const imageHeight = this.parallelogramHeightCalculation(this.zdcMap, latlons)
    const drawLatlon = this.widgetRollLatlonCalculation(this.zdcMap, latlons)
    const rotation = this.widgetRollRadiusCalculation(this.zdcMap, latlons)

    const parkingLatLon = [new ZDC.LatLon(drawLatlon.lat, drawLatlon.lon)]

    const options = {
      html:
        '<img style="width: ' +
        imageWidth +
        'px; height:' +
        imageHeight +
        'px;" src="' +
        parkingDataItem.imagePath +
        '"></img>',
      rotation: rotation,
      editable: false
    }

    if (!this.groundImage[parkingDataItem.id]) {
      this.groundImage[parkingDataItem.id] = new ZDC.EditableUserWidget(this.zdcMap, parkingLatLon, options)
    }

    this.groundImage[parkingDataItem.id].setEditable()
  }

  /**
   * 写真を削除する
   *
   * @param {ManagementPlaceMapData} parkingDataItem 管理場所
   * @returns {void}
   */
  removeImage(parkingDataItem: ManagementPlaceMapData): void {
    if (!parkingDataItem || !parkingDataItem.id || !parkingDataItem.imagePath) {
      return
    }
    if (!this.groundImage) {
      this.groundImage = {}
    }
    this.groundImage[parkingDataItem.id].remove()
  }

  /**
   * フリーワードでの住所検索
   *
   * @param {string} keyword 検索キーワード
   * @param {boolean} isErrorMsg エラーメッセージを表示するかどうか
   * @param {any} callback callback
   * @returns {void}
   */
  // eslint-disable-next-line
  searchAddressInfo(keyword: string, isErrorMsg: boolean, callback: (res: any) => void): void {
    ZDC.Search.getByZmaps(
      'address/word',
      {
        word: keyword
      },
      // eslint-disable-next-line
      (status: any, res: any) => {
        if (status.code === 200 && res.status.code === '0000' && res.item && res.item.length > 0) {
          /* 取得成功 */
          callback(res.item)
        } else {
          if (isErrorMsg) {
            msgBoxService.error(i18n.t('locationInfo.searchError').toString())
          }
          callback(null)
        }
      }
    )
  }

  /**
   * 郵便番号情報検索
   *
   * @param {string} zipCode 郵便番号
   * @param {any} callback callback
   * @returns {void}
   */
  // eslint-disable-next-line
  searchZipCodeInfo(zipCode: string, callback: (res: any) => void): void {
    ZDC.Search.getByZmaps(
      'address/zipcode',
      {
        zipcode: zipCode
      },
      // eslint-disable-next-line
      (status: any, res: any) => {
        if (status.code === 200 && res.item && res.item.length > 0) {
          /* 取得成功 */
          callback(res.item)
        } else {
          msgBoxService.error(i18n.t('locationInfo.searchError').toString())
          callback(null)
        }
      }
    )
  }

  /**
   * 緯度経度での住所検索
   *
   * @param {Position} paramLatLon 検緯度経度
   * @param {any} callback callback
   * @returns {void}
   */
  // eslint-disable-next-line
  searchAddressInfoByLatlon(paramLatLon: Position, callback: (res: any) => void): void {
    const latlon = paramLatLon.lat + ',' + paramLatLon.lng
    ZDC.Search.getByZmaps(
      'location',
      {
        latlon: latlon
      },
      // eslint-disable-next-line
      (status: any, res: any) => {
        if (status.code === 200 && res && res.length > 0) {
          /* 取得成功 */
          callback(res[0]?.address)
        } else {
          callback(null)
        }
      }
    )
  }

  /**
   * 指定した緯度経度を地図の中心に
   *
   * @param {Position} paramLatLon 緯度経度
   * @returns {void}
   */
  moveLatLon(paramLatLon: Position): void {
    const objectLatLon = new ZDC.LatLon(paramLatLon.lat, paramLatLon.lng)
    this.zdcMap?.moveLatLon(objectLatLon)
  }

  /**
   * 管理対象データを取得する
   *
   * @param {ManagementItem[]} dynamicTargetList 管理対象データ
   */
  getDynamicTargetList(dynamicTargetList: ManagementItem[]): void {
    this.dynamicTargetList = dynamicTargetList
  }

  /**
   * ウィジットのz-indexを変更して表示順を変更する
   *
   * @param {string} id 管理対象ID
   * @returns {void}
   */
  setManagementIndex(id: string): void {
    if (!this.groundObject) return

    for (const key in this.groundObject) {
      const element = this.groundObject[key]
      element.setZindex(100)
    }
    this.groundObject[id].setZindex(101)
  }

  /**
   * 四辺形のwidthを計算(px)
   *
   * @param {any} currentMap 地図
   * @param {any} totkyOriginLatlon 位置情報
   * @returns {void}
   */
  // eslint-disable-next-line
  parallelogramWidthCalculation(currentMap: any, totkyOriginLatlon: any[]) {
    /* 緯度経度をpxに変換*/
    const TL1 = currentMap.latLonToTL(totkyOriginLatlon[0])
    const TL2 = currentMap.latLonToTL(totkyOriginLatlon[1])

    const topDiff = Math.abs(TL1.top - TL2.top)
    const leftDiff = Math.abs(TL1.left - TL2.left)

    //2点間の長さの計算
    return Math.sqrt(Math.pow(topDiff, 2) + Math.pow(leftDiff, 2))
  }

  /**
   * 四辺形のheightを計算(px)
   *
   * @param {any} currentMap 地図
   * @param {any} totkyOriginLatlon 位置情報
   * @returns {void}
   */
  // eslint-disable-next-line
  parallelogramHeightCalculation(currentMap: any, totkyOriginLatlon: any[]) {
    /* 緯度経度をpxに変換*/
    const TL1 = currentMap.latLonToTL(totkyOriginLatlon[1])
    const TL2 = currentMap.latLonToTL(totkyOriginLatlon[2])

    const topDiff = Math.abs(TL1.top - TL2.top)
    const leftDiff = Math.abs(TL1.left - TL2.left)

    //2点間の長さの計算
    return Math.sqrt(Math.pow(topDiff, 2) + Math.pow(leftDiff, 2))
  }

  /**
   * 回転なユーザーウィジェットの緯度経度を計算
   *
   * @param {any} currentMap 地図
   * @param {any} totkyOriginLatlon 位置情報
   * @returns {void}
   */
  // eslint-disable-next-line
  widgetRollLatlonCalculation(currentMap: any, totkyOriginLatlon: any[]) {
    /* 緯度経度をpxに変換*/
    const TL = []
    TL.push(currentMap.latLonToTL(totkyOriginLatlon[0]))
    TL.push(currentMap.latLonToTL(totkyOriginLatlon[1]))
    TL.push(currentMap.latLonToTL(totkyOriginLatlon[2]))

    /* 四辺形の円の座標を計算(px) */
    const TLcircle = ZDC.TL((TL[0].top + TL[2].top) / 2, (TL[0].left + TL[2].left) / 2)

    /* 四辺形のwidthを計算(px) */
    const widthTmp = this.parallelogramWidthCalculation(currentMap, totkyOriginLatlon)

    /* 四辺形のheightを計算(px) */
    const heightTmp = this.parallelogramHeightCalculation(currentMap, totkyOriginLatlon)

    /* 回転なユーザーウィジェットの座標(px)を計算 */
    const TLTmp = ZDC.TL(TLcircle.top - heightTmp / 2, TLcircle.left - widthTmp / 2)

    /* 回転なユーザーウィジェットの緯度経度を計算 */
    return currentMap.tlToLatLon(TLTmp)
  }

  /**
   * 回転なユーザーウィジェットの回転角度を計算
   *
   * @param {any} currentMap 地図
   * @param {any} totkyOriginLatlon 位置情報
   * @returns {void}
   */
  // eslint-disable-next-line
  widgetRollRadiusCalculation(currentMap: any, totkyOriginLatlon: any[]) {
    /* 緯度経度をpxに変換*/
    const TL = []
    TL.push(currentMap.latLonToTL(totkyOriginLatlon[0]))
    TL.push(currentMap.latLonToTL(totkyOriginLatlon[1]))
    TL.push(currentMap.latLonToTL(totkyOriginLatlon[2]))
    /* 四辺形の円の座標を計算(px) */
    const TLcircle = ZDC.TL((TL[0].top + TL[2].top) / 2, (TL[0].left + TL[2].left) / 2)
    /* 四辺形のwidthを計算(px) */
    const widthTmp = this.parallelogramWidthCalculation(currentMap, totkyOriginLatlon)

    /* 四辺形のheightを計算(px) */
    const heightTmp = this.parallelogramHeightCalculation(currentMap, totkyOriginLatlon)

    /* 回転なユーザーウィジェットの座標(px)を計算 */
    const TLTmp = ZDC.TL(TLcircle.top - heightTmp / 2, TLcircle.left - widthTmp / 2)

    /* 回転角度が0場合 */
    if (TL[0].top == TL[1].top) {
      return 0
    }

    // 四辺形の円の半径を計算
    const topDiff = Math.abs(TL[2].top - TL[0].top)
    const leftDiff = Math.abs(TL[2].left - TL[0].left)
    const radiusOfCircle = Math.sqrt(Math.pow(topDiff, 2) + Math.pow(leftDiff, 2)) / 2

    // 回転角度を計算
    const sinOfAngle1 = Math.abs(TLcircle.left - TL[0].left) / radiusOfCircle
    const rotation1 = (Math.asin(sinOfAngle1) * 180) / Math.PI

    const sinOfAngle2 = Math.abs(TLcircle.left - TLTmp.left) / radiusOfCircle
    const rotation2 = (Math.asin(sinOfAngle2) * 180) / Math.PI

    return rotation2 - rotation1
  }

  /**
   * 地図のプロット点を描画する
   *
   * @param {DynamicHistory} historyItem 移動履歴
   * @returns {void}
   */
  markPlotPoint(historyItem: DynamicHistory): void {
    this.removePlotPoints()

    if (!historyItem || !historyItem.lat || !historyItem.lng) {
      return
    }

    if (!this.plotPoints) {
      this.plotPoints = {}
    }

    if (!this.plotPoints[historyItem.moveHistoryId]) {
      this.plotPoints[historyItem.moveHistoryId] = {}
    }

    const objectLatLon = new ZDC.LatLon(historyItem.lat, historyItem.lng)

    /* マーカを作成 */
    this.plotPoints[historyItem.moveHistoryId].point = new ZDC.Marker(objectLatLon, {
      custom: {
        base: {
          src: '/images/wo_050.png'
        }
      },
      offset: new ZDC.Pixel(-16, -32)
    })

    /* マーカを追加 */
    this.zdcMap?.addWidget(this.plotPoints[historyItem.moveHistoryId].point)

    const widgetlabel = {
      offset: new ZDC.Pixel(5, -5),
      html: '<div style="background-color: #FFFFFF"><b>' + historyItem.number + '</b></div>'
    }
    this.plotPoints[historyItem.moveHistoryId].name = new ZDC.UserWidget(objectLatLon, widgetlabel)

    /* ユーザウィジットを追加 */
    this.zdcMap?.addWidget(this.plotPoints[historyItem.moveHistoryId].name)

    /* ユーザウィジットを表示 */
    this.plotPoints[historyItem.moveHistoryId]?.name?.open()

    const paramLatLon = { lat: objectLatLon.lat, lon: objectLatLon.lon }
    this.zdcMap?.moveLatLon(paramLatLon)
  }

  /**
   * 地図をクリックしたときの動作を描画する
   *
   * @param {any} latlon クリックした地点の緯度経度
   * @returns {void}
   */
  // eslint-disable-next-line
  markClickedPoint(latlon: any): void {
    if (this.clickedPoint) {
      this.zdcMap?.removeWidget(this.clickedPoint)
    }
    /* マーカを作成 */
    this.clickedPoint = new ZDC.Marker(latlon, {
      custom: {
        base: {
          src: '/images/clicked.png'
        }
      },
      offset: new ZDC.Pixel(-16, -16)
    })

    /* マーカを追加 */
    this.zdcMap?.addWidget(this.clickedPoint)
  }
}
